import { cn } from "@/lib/utils";
import {
  ColumnDef,
  RowData,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    sticky?: "right";
  }
}
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Loader2,
} from "lucide-react";
import { CSSProperties, useState } from "react";
import { exportTableToCsv } from "@/lib/export-csv";

export interface CustomTableProps<TData> {
  rows: TData[];
  columns: ColumnDef<TData>[];
  isLoading: boolean;
  overlayText: string;
  onRowClick?: (row: TData) => void;
  checkboxSelection?: boolean;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  isRowSelectable?: (row: TData) => boolean;
  hideFooter?: boolean;
  getRowId?: (row: TData) => string;
  rowHeight?: number;
  pagination?: boolean;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  className?: string;
  exportFileName?: string;
  defaultPageSize?: number;
}

function CustomTable<TData>({
  rows,
  columns,
  isLoading,
  overlayText,
  onRowClick,
  checkboxSelection = false,
  onRowSelectionChange,
  isRowSelectable,
  hideFooter = false,
  getRowId,
  pagination = true,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  className,
  exportFileName,
  defaultPageSize = 10,
}: CustomTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [internalColumnVisibility, setInternalColumnVisibility] =
    useState<VisibilityState>({});

  const columnVisibility =
    controlledColumnVisibility ?? internalColumnVisibility;
  const handleColumnVisibilityChange =
    onColumnVisibilityChange ?? setInternalColumnVisibility;

  const collectExplicitSizes = (
    defs: ColumnDef<TData>[],
    sized: Set<string>,
    minSized: Map<string, number>,
  ) => {
    defs.forEach((def) => {
      const key = def.id ?? (def as { accessorKey?: string }).accessorKey;
      if (key) {
        if (def.size !== undefined) sized.add(key);
        if (def.minSize !== undefined) minSized.set(key, def.minSize);
      }
      const children = (def as { columns?: ColumnDef<TData>[] }).columns;
      if (children) collectExplicitSizes(children, sized, minSized);
    });
  };
  const sizedColumnIds = new Set<string>();
  const minSizedColumnIds = new Map<string, number>();
  collectExplicitSizes(columns, sizedColumnIds, minSizedColumnIds);
  if (checkboxSelection) sizedColumnIds.add("select");

  const allColumns: ColumnDef<TData>[] = checkboxSelection
    ? [
        {
          id: "select",
          header: ({ table }) => (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() && "indeterminate")
              }
              onCheckedChange={(value) =>
                table.toggleAllPageRowsSelected(!!value)
              }
              aria-label="Select all"
            />
          ),
          cell: ({ row }) => (
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onCheckedChange={(value) => row.toggleSelected(!!value)}
              aria-label="Select row"
            />
          ),
          enableSorting: false,
          size: 40,
        },
        ...columns,
      ]
    : columns;

  const table = useReactTable({
    data: rows,
    columns: allColumns,
    state: {
      sorting,
      rowSelection,
      columnVisibility,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(next);
      onRowSelectionChange?.(next);
    },
    onColumnVisibilityChange: (updater) => {
      const next =
        typeof updater === "function" ? updater(columnVisibility) : updater;
      handleColumnVisibilityChange(next);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(pagination && {
      getPaginationRowModel: getPaginationRowModel(),
      initialState: { pagination: { pageSize: defaultPageSize } },
    }),
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
    enableRowSelection: isRowSelectable
      ? (row) => isRowSelectable(row.original)
      : checkboxSelection,
  });

  return (
    <div className={cn("w-full", className)}>
      {exportFileName && (
        <div className="flex justify-end pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportTableToCsv(table, exportFileName)}
            disabled={isLoading || table.getRowModel().rows.length === 0}
          >
            <Download />
            Export CSV
          </Button>
        </div>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isLeaf = header.subHeaders.length === 0;
                  const sticky = header.column.columnDef.meta?.sticky;
                  const hasExplicitSize = sizedColumnIds.has(header.column.id);
                  const size = hasExplicitSize
                    ? header.getSize()
                    : undefined;
                  const minSize = minSizedColumnIds.get(header.column.id);
                  const style: CSSProperties = {};
                  if (size !== undefined) {
                    style.width = size;
                    style.maxWidth = size;
                  }
                  if (minSize !== undefined) style.minWidth = minSize;
                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={
                        Object.keys(style).length > 0 ? style : undefined
                      }
                      className={cn(
                        sticky === "right" &&
                          "sticky right-0 bg-background z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]",
                      )}
                    >
                      {header.isPlaceholder ? null : isLeaf &&
                        header.column.getCanSort() ? (
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:text-foreground"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <ArrowUpDown className="size-3" />
                        </button>
                      ) : (
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={allColumns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="size-4 animate-spin" />
                    <span className="text-muted-foreground">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={allColumns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {overlayText}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={cn("group/row", onRowClick && "cursor-pointer")}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const sticky = cell.column.columnDef.meta?.sticky;
                    const hasExplicitSize = sizedColumnIds.has(cell.column.id);
                    const size = hasExplicitSize
                      ? cell.column.getSize()
                      : undefined;
                    const minSize = minSizedColumnIds.get(cell.column.id);
                    const style: CSSProperties = {};
                    if (size !== undefined) {
                      style.width = size;
                      style.maxWidth = size;
                    }
                    if (minSize !== undefined) style.minWidth = minSize;
                    return (
                      <TableCell
                        key={cell.id}
                        style={
                          Object.keys(style).length > 0 ? style : undefined
                        }
                        className={cn(
                          "overflow-hidden",
                          sticky === "right" &&
                            "sticky right-0 bg-background z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover/row:bg-muted/50 group-data-[state=selected]/row:bg-muted",
                        )}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && !hideFooter && (
        <div className="flex items-center justify-between py-2">
          <div className="text-muted-foreground text-xs">
            {checkboxSelection && (
              <span>
                {table.getFilteredSelectedRowModel().rows.length} of{" "}
                {table.getFilteredRowModel().rows.length} row(s) selected.
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-full">
                Rows per page
              </span>
              <Select
                value={String(table.getState().pagination.pageSize)}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground">
              Page {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronsLeft />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                <ChevronRight />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <ChevronsRight />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomTable;
